//pages/Profile.tsx
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useMutation } from '@tanstack/react-query';
import Layout from '@/components/Layout';
import SEO from '@/components/SEO'; // SEO: profile private -> noindex,nofollow
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { User, Lock, LogOut, AlertTriangle, CreditCard, Trash2, Smartphone, Monitor, Globe, Eye, EyeOff, ChevronDown, ChevronUp, ShieldCheck, ArrowUpDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { isUnauthorizedError } from '@/lib/authUtils';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useCurrentPackage, useDeletePaymentMethod, usePaymentMethods, useSetDefaultPaymentMethod, usePaymentHistory } from '@/hooks/usePayments';
import { formatDistanceToNow, format } from 'date-fns';
import { apiRequest } from '@/lib/queryClient';
// Add card modal removed per request

// (User type inferred from hook; explicit interface removed)

export default function Profile() {
  const { user, isLoading, sessionsQuery, revokeSessionMutation, logoutMutation, currentSessionId, logout } = useAuth() as any;
  const { toast } = useToast();

  // Hosted add-card return handler removed

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });
  const [isPasswordSectionOpen, setIsPasswordSectionOpen] = useState(false);
  const [isPaymentSectionOpen, setIsPaymentSectionOpen] = useState(false);
  const [isDevicesSectionOpen, setIsDevicesSectionOpen] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{type: 'success' | 'error' | null, text: string}>({ type: null, text: '' });

  // Payments
  const methodsQuery = usePaymentMethods();
  const deleteMethod = useDeletePaymentMethod();
  const makeDefault = useSetDefaultPaymentMethod();
  const currentPackage = useCurrentPackage();
  const historyQuery = usePaymentHistory();
  

  const [showAllSessions, setShowAllSessions] = useState(false);
  const devices = useMemo<any[]>(() => {
    if (!sessionsQuery?.data) return [];
    const rawData = Array.isArray(sessionsQuery.data) ? sessionsQuery.data : sessionsQuery.data.sessions || [];
    const active = rawData.filter((s: any) => {
      if (typeof s.is_active === 'boolean') return s.is_active;
      if (s.status) return s.status === 'active';
      return true;
    });
    // Group by device_id (fallback to composite signature)
    const groups = new Map<string, any[]>();
    for (const s of active) {
      const key = s.device_id || `${s.browser||''}|${s.os||''}|${s.ip_address||''}|${s.remember_me?'1':'0'}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(s);
    }
    const representatives: any[] = [];
    groups.forEach(list => {
      // Pick most recent last_seen / updated
      list.sort((a:any,b:any)=> new Date(b.last_seen_at||b.updated_at||b.created_at||0).getTime() - new Date(a.last_seen_at||a.updated_at||a.created_at||0).getTime());
      representatives.push(list[0]);
    });
    const toRender = showAllSessions ? active : representatives;
    return toRender.map((s: any) => {
      const location = [s.location_city, s.location_country].filter(Boolean).join(', ');
      return {
        id: s.id,
        name: s.device_name || [s.os, s.browser].filter(Boolean).join(' – ') || 'Unknown Device',
        location: location || 'Unknown location',
        ip: s.ip_address || '—',
        icon: /iphone|ios|mobile|android/i.test(`${s.device_name} ${s.browser} ${s.os}`) ? 'phone' : 'monitor',
        lastSeen: s.last_seen_at || s.updated_at || s.created_at,
        raw: s,
        isCurrent: s.id === currentSessionId || s.session_id === currentSessionId,
      };
    });
  }, [sessionsQuery?.data, currentSessionId, showAllSessions]);

  const uniqueCount = useMemo(()=> {
    if (!sessionsQuery?.data) return 0;
    const rawData = Array.isArray(sessionsQuery.data) ? sessionsQuery.data : sessionsQuery.data.sessions || [];
    const keys = new Set<string>();
    for (const s of rawData) {
      if (typeof s.is_active === 'boolean' && !s.is_active) continue;
      if (s.status && s.status !== 'active') continue;
      const key = s.device_id || `${s.browser||''}|${s.os||''}|${s.ip_address||''}|${s.remember_me?'1':'0'}`;
      keys.add(key);
    }
    return keys.size;
  }, [sessionsQuery?.data]);
  const totalActive = useMemo(()=> devices.length, [devices]);
  const [revealDeviceIds, setRevealDeviceIds] = useState(false);
  // Add card modal/state removed
  const [confirmDelete, setConfirmDelete] = useState<{open: boolean; id?: string|number}>({ open: false });
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [sortBy, setSortBy] = useState<'date'|'amount'|'package'>('date');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('desc');
  const methodsById = useMemo(() => {
    const map = new Map<string, any>();
    (methodsQuery.data || []).forEach(m => map.set(String(m.id), m));
    return map;
  }, [methodsQuery.data]);
  const defaultMethodSummary = useMemo(() => {
    const methods = methodsQuery.data || [];
    if (!methods.length) return null;
    const def = methods.find(m => m.is_default) || methods[0];
    const last4 = def.last4 ? String(def.last4).replace(/\D/g, '').slice(-4) : '';
    const scheme = def.scheme || 'Card';
    if (!last4) return scheme;
    return `${scheme} •••• ${last4}`;
  }, [methodsQuery.data]);

  // Paddle checkout result modal
  const [checkoutModal, setCheckoutModal] = useState<{ open: boolean; success?: boolean; message?: string }>({ open: false });
  // Helper: await completion via /wait with polling fallback, then show modal
  // awaitUntilComplete removed alongside checkout flow
  // startPaddleCheckout removed along with Add Tokens button

  // Password strength calculator
  // Visual-only strength (does not gate submission). Mirrors Register minimal policy.
  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return { score: 0, label: '', color: 'text-gray-400' };
    const lengthScore = pwd.length >= 12 ? 2 : pwd.length >= 8 ? 1 : 0;
    const variety = [/[_a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].reduce((acc, r) => acc + (r.test(pwd) ? 1 : 0), 0);
    const score = lengthScore + variety; // 0-6 approx
    if (pwd.length >= 6 && score >= 5) return { score: 100, label: 'Strong', color: 'text-green-400' };
    if (pwd.length >= 6 && score >= 3) return { score: 60, label: 'Medium', color: 'text-amber-300' };
    return { score: 30, label: 'Weak', color: 'text-red-400' };
  };
  const passwordStrength = getPasswordStrength(newPassword);
  const passwordTooShort = newPassword.length > 0 && newPassword.length < 6;
  const hasUppercase = /[A-Z]/.test(newPassword);
  const confirmMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const minimalOk = newPassword.length >= 6 && hasUppercase && !confirmMismatch && newPassword !== currentPassword;

  // Redirect if no user (unauthorized)
  useEffect(() => {
    if (!isLoading && !user) {
      toast({ title: 'Unauthorized', description: 'You are logged out. Logging in again...', variant: 'destructive' });
      setTimeout(() => { window.location.href = '/login'; }, 500);
    }
  }, [user, isLoading, toast]);

  // Additional safeguard: if sessions query fails with 401, redirect
  useEffect(() => {
    if (sessionsQuery?.error) {
      const msg = String((sessionsQuery.error as any)?.message || '');
      if (msg.startsWith('401')) {
        toast({ title: 'Session expired', description: 'Please login again.', variant: 'destructive' });
        setTimeout(()=> window.location.href='/login', 400);
      }
    }
  }, [sessionsQuery?.error, toast]);

  // Real password update using API endpoint
  const updatePasswordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string; confirmPassword: string }) => {
      const response = await apiRequest('PUT', '/api/user/change-password', {
        current_password: data.currentPassword,
        new_password: data.newPassword,
        confirm_new_password: data.confirmPassword
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || 'Failed to update password');
      }

      return await response.json();
    },
    onSuccess: () => {
      // Set success message in the card
      setPasswordMessage({ type: 'success', text: 'Password has been successfully changed.' });
      
      // Also show toast for immediate feedback
      toast({ 
        title: 'Password Updated Successfully', 
        description: 'Your password has been successfully changed.',
        className: 'bg-green-800 border-green-600 text-green-100'
      });
      
      setCurrentPassword(''); 
      setNewPassword(''); 
      setConfirmPassword('');
      
      // Clear the message after 5 seconds
      setTimeout(() => {
        setPasswordMessage({ type: null, text: '' });
      }, 5000);
      
      // Optional: Auto logout after password change for security
      setTimeout(() => {
        toast({ 
          title: 'Security Notice', 
          description: 'Please sign in again with your new password.', 
          variant: 'default' 
        });
        logout();
        window.location.href = '/login';
      }, 2000);
    },
    onError: (error: any) => {
      if (isUnauthorizedError(error)) {
        toast({ 
          title: 'Session Expired', 
          description: 'Please sign in again to change your password.', 
          variant: 'destructive' 
        });
        setTimeout(() => { window.location.href = '/login'; }, 500);
        return;
      }
      
      // Parse specific error messages from API
      let errorMessage = 'Failed to update password. Please try again.';
      const errorText = error.message || '';
      
      if (errorText.includes('current password') || errorText.includes('incorrect password')) {
        errorMessage = 'Current password is incorrect. Please check your password and try again.';
      } else if (errorText.includes('passwords do not match')) {
        errorMessage = 'New passwords do not match.';
      } else if (errorText.includes('same as current')) {
        errorMessage = 'New password must be different from current password.';
      } else if (errorText.includes('at least 8 characters') || errorText.includes('at least 6')) {
        errorMessage = 'Password must be at least 6 characters long.';
      } else if (errorText.includes('too weak') || /uppercase/.test(errorText)) {
        errorMessage = 'Password must contain at least one uppercase letter (A-Z).';
      }
      
      // Set error message in the card
      setPasswordMessage({ type: 'error', text: errorMessage });
      
      // Also show toast for immediate feedback
      toast({ 
        title: 'Password Update Failed', 
        description: errorMessage, 
        variant: 'destructive' 
      });
      
      // Clear the message after 5 seconds
      setTimeout(() => {
        setPasswordMessage({ type: null, text: '' });
      }, 5000);
    }
  });

  const handlePasswordUpdate = () => {
    // Clear any previous messages
    setPasswordMessage({ type: null, text: '' });
    
    // Client-side validation (same as backend for better UX)
    if (!currentPassword || !newPassword || !confirmPassword) {
      const errorMsg = 'Please fill in all password fields.';
      setPasswordMessage({ type: 'error', text: errorMsg });
      toast({ 
        title: 'Validation Error', 
        description: errorMsg, 
        variant: 'destructive' 
      });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      const errorMsg = 'New passwords do not match.';
      setPasswordMessage({ type: 'error', text: errorMsg });
      toast({ 
        title: 'Validation Error', 
        description: errorMsg, 
        variant: 'destructive' 
      });
      return;
    }
    
    if (newPassword.length < 6) {
      const errorMsg = 'Password must be at least 6 characters long.';
      setPasswordMessage({ type: 'error', text: errorMsg });
      toast({ 
        title: 'Validation Error', 
        description: errorMsg, 
        variant: 'destructive' 
      });
      return;
    }
    if (!hasUppercase) {
      const errorMsg = 'Password must contain at least one uppercase letter (A-Z).';
      setPasswordMessage({ type: 'error', text: errorMsg });
      toast({ title: 'Validation Error', description: errorMsg, variant: 'destructive' });
      return;
    }
    
    if (newPassword === currentPassword) {
      const errorMsg = 'New password must be different from current password.';
      setPasswordMessage({ type: 'error', text: errorMsg });
      toast({ 
        title: 'Validation Error', 
        description: errorMsg, 
        variant: 'destructive' 
      });
      return;
    }
    
    // All validations passed, proceed with API call
    updatePasswordMutation.mutate({ 
      currentPassword, 
      newPassword, 
      confirmPassword 
    });
  };

  // Payment methods handlers (mock)
  const removePaymentMethod = async (id: string | number) => {
    try {
      await deleteMethod.mutateAsync({ id });
      toast({ title: 'Removed', description: 'Payment method removed.' });
    } catch (e: any) {
      toast({ title: 'Failed to remove', description: e?.message || 'Try again.', variant: 'destructive' });
    }
  };
  // Card addition handled via AddCardModal (Paddle Checkout) instead of prompt

  // Full account logout
  const [revokeDialog, setRevokeDialog] = useState<{ open: boolean; sessionId?: number | string | null }>({ open: false, sessionId: null });

  const handleLogout = () => {
    // If we have a current session id, attempt revoke first for backend hygiene
    if (currentSessionId && revokeSessionMutation) {
      revokeSessionMutation.mutate(Number(currentSessionId), {
        onSettled: () => {
          logoutMutation?.mutate(undefined, {
            onSettled: () => {
              logout();
              window.location.href = '/login';
            }
          });
        }
      });
    } else {
      logoutMutation?.mutate(undefined, {
        onSettled: () => {
          logout();
          window.location.href = '/login';
        }
      });
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen"><LoadingSpinner /></div>
      </Layout>
    );
  }
  if (!user) return null;

  const normalizedFirstName = (user as any)?.first_name ?? user.firstName ?? '';
  const normalizedLastName = (user as any)?.last_name ?? (user as any)?.lastName ?? '';

  return (
    <Layout>
      <SEO title="Profile Settings | TradeSagePro" description="Manage account security, sessions, payment methods and preferences securely." canonical="https://tradesagepro.com/profile" noindex nofollow />
      <div className="min-h-screen bg-[hsl(226,26%,7%)] text-[hsl(210,9%,91%)] py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-4">Profile Settings</h1>
            <p className="text-[hsl(210,9%,91%)]">Manage your account settings and preferences</p>
          </div>

          <div className="space-y-6">
            {/* Personal Information */}
            <Card className="bg-[hsl(215,15%,18%)] border-gray-700 animate-slide-in">
              <CardHeader>
                <CardTitle className="text-white flex items-center"><User className="w-5 h-5 mr-2 text-[hsl(193,100%,50%)]" />Personal Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[hsl(210,9%,91%)]">Name</Label>
                    <Input value={normalizedFirstName} disabled className="mt-1 bg-[hsl(226,26%,7%)] border-gray-600 text-white" />
                  </div>
                  <div>
                    <Label className="text-[hsl(210,9%,91%)]">Surname</Label>
                    <Input value={normalizedLastName} disabled className="mt-1 bg-[hsl(226,26%,7%)] border-gray-600 text-white" />
                  </div>
                </div>
                <div className="mt-4">
                  <Label className="text-[hsl(210,9%,91%)]">Email</Label>
                  <div className="mt-1 text-sm text-[hsl(210,9%,91%)] bg-[hsl(226,26%,7%)] border border-gray-600 rounded-md p-3">{user.email || '—'}</div>
                </div>
              </CardContent>
            </Card>

            {/* Change Password */}
            <Card className="bg-[hsl(215,15%,18%)] border-gray-700 animate-slide-in">
              <CardHeader 
                className="cursor-pointer hover:bg-[hsl(215,15%,20%)] transition-colors"
                onClick={() => setIsPasswordSectionOpen(!isPasswordSectionOpen)}
              >
                <CardTitle className="text-white flex items-center justify-between">
                  <div className="flex items-center">
                    <Lock className="w-5 h-5 mr-2 text-[hsl(193,100%,50%)]" />
                    Change Password
                  </div>
                  {isPasswordSectionOpen ? (
                    <ChevronUp className="w-5 h-5 text-[hsl(193,100%,50%)] transition-transform" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[hsl(193,100%,50%)] transition-transform" />
                  )}
                </CardTitle>
              </CardHeader>
              {isPasswordSectionOpen && (
                <CardContent className="space-y-4 animate-in slide-in-from-top-2 duration-200">
                  {/* Password Change Message */}
                  {passwordMessage.type && (
                    <div className={`p-3 rounded-lg border text-sm ${
                      passwordMessage.type === 'success' 
                        ? 'bg-green-900/20 border-green-600 text-green-400' 
                        : 'bg-red-900/20 border-red-600 text-red-400'
                    } animate-in fade-in-50 duration-200`}>
                      {passwordMessage.text}
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="current-password" className="text-[hsl(210,9%,91%)]">Current Password</Label>
                    <div className="relative mt-1">
                      <Input 
                        id="current-password" 
                        type={showPw.current ? 'text' : 'password'} 
                        value={currentPassword} 
                        onChange={e => {
                          setCurrentPassword(e.target.value);
                          if (passwordMessage.type) setPasswordMessage({ type: null, text: '' });
                        }} 
                        className="pr-10 bg-[hsl(226,26%,7%)] border-gray-600 text-white focus:border-[hsl(193,100%,50%)]" 
                        placeholder="Enter current password" 
                      />
                      <button type="button" onClick={() => setShowPw(p => ({ ...p, current: !p.current }))} className="absolute inset-y-0 right-0 px-2 flex items-center text-gray-400 hover:text-white" aria-label={showPw.current ? 'Hide password' : 'Show password'}>
                        {showPw.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="new-password" className="text-[hsl(210,9%,91%)]">New Password</Label>
                    <div className="relative mt-1">
                      <Input 
                        id="new-password" 
                        type={showPw.next ? 'text' : 'password'} 
                        value={newPassword} 
                        onChange={e => {
                          setNewPassword(e.target.value);
                          if (passwordMessage.type) setPasswordMessage({ type: null, text: '' });
                        }} 
                        className="pr-10 bg-[hsl(226,26%,7%)] border-gray-600 text-white focus:border-[hsl(193,100%,50%)]" 
                        placeholder="Enter new password" 
                      />
                      <button type="button" onClick={() => setShowPw(p => ({ ...p, next: !p.next }))} className="absolute inset-y-0 right-0 px-2 flex items-center text-gray-400 hover:text-white" aria-label={showPw.next ? 'Hide password' : 'Show password'}>
                        {showPw.next ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {/* Password Strength Indicator & Minimal Requirements */}
                    {newPassword && (
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-400">Password Strength</span>
                          <span className={`font-medium ${passwordStrength.color}`}>{passwordStrength.label}</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${
                              passwordStrength.label === 'Strong' ? 'bg-green-500 w-full' :
                              passwordStrength.label === 'Medium' ? 'bg-amber-400 w-2/3' :
                              'bg-red-500 w-1/3'
                            }`}
                          ></div>
                        </div>
                        <div className="text-xs space-y-1">
                          {passwordTooShort && <p className="text-red-400">Password must be at least 6 characters.</p>}
                          {!passwordTooShort && !hasUppercase && <p className="text-red-400">Password must contain at least one uppercase letter (A-Z).</p>}
                          {!passwordTooShort && hasUppercase && newPassword.length >= 6 && newPassword.length < 8 && <p className="text-gray-400">Optional: add more variety for a stronger password.</p>}
                          {newPassword === currentPassword && <p className="text-red-400">New password must be different from current password.</p>}
                          {!confirmMismatch && minimalOk && <p className="text-green-400">Looks good.</p>}
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="confirm-password" className="text-[hsl(210,9%,91%)]">Confirm New Password</Label>
                    <div className="relative mt-1">
                      <Input 
                        id="confirm-password" 
                        type={showPw.confirm ? 'text' : 'password'} 
                        value={confirmPassword} 
                        onChange={e => {
                          setConfirmPassword(e.target.value);
                          if (passwordMessage.type) setPasswordMessage({ type: null, text: '' });
                        }} 
                        className="pr-10 bg-[hsl(226,26%,7%)] border-gray-600 text-white focus:border-[hsl(193,100%,50%)]" 
                        placeholder="Confirm new password" 
                      />
                      <button type="button" onClick={() => setShowPw(p => ({ ...p, confirm: !p.confirm }))} className="absolute inset-y-0 right-0 px-2 flex items-center text-gray-400 hover:text-white" aria-label={showPw.confirm ? 'Hide password' : 'Show password'}>
                        {showPw.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button onClick={handlePasswordUpdate} disabled={updatePasswordMutation.isPending} className="w-full bg-[hsl(193,100%,50%)] text-[hsl(226,26%,7%)] hover:scale-105 transition-all duration-300">
                    {updatePasswordMutation.isPending ? (<><LoadingSpinner className="w-4 h-4 mr-2" />Updating Password...</>) : 'Update Password'}
                  </Button>
                </CardContent>
              )}
            </Card>

            {/* Payment Methods */}
            <Card className="bg-[hsl(215,15%,18%)] border-gray-700 animate-slide-in">
              <CardHeader 
                className="cursor-pointer hover:bg-[hsl(215,15%,20%)] transition-colors"
                onClick={() => setIsPaymentSectionOpen(!isPaymentSectionOpen)}
              >
                <CardTitle className="text-white flex items-center justify-between">
                  <div className="flex items-center">
                    <CreditCard className="w-5 h-5 mr-2 text-[hsl(193,100%,50%)]" />
                    Payment Methods
                  </div>
                  {isPaymentSectionOpen ? (
                    <ChevronUp className="w-5 h-5 text-[hsl(193,100%,50%)] transition-transform" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[hsl(193,100%,50%)] transition-transform" />
                  )}
                </CardTitle>
              </CardHeader>
              {isPaymentSectionOpen && (
                <CardContent className="space-y-3 animate-in slide-in-from-top-2 duration-200">
                  {/* Current package/tokens mini header */}
                  {currentPackage.data && (
                    <div className="text-xs text-gray-300">
                      Active Pack: {currentPackage.data.package_name || currentPackage.data.package_code || '—'} • Tokens: <span className="text-[hsl(193,100%,50%)] font-semibold">{currentPackage.data.credits_balance}</span>
                    </div>
                  )}

                  {defaultMethodSummary && (
                    <div className="text-sm text-gray-200">
                      Payment Method: <span className="font-semibold">{defaultMethodSummary}</span>
                    </div>
                  )}

                  {methodsQuery.isLoading && <div className="text-sm text-gray-400">Loading cards…</div>}
                  {methodsQuery.isError && <div className="text-sm px-3 py-2 rounded bg-red-600/20 border border-red-500/40 text-white">Failed to load cards.</div>}
                  {!methodsQuery.isLoading && (methodsQuery.data?.length ?? 0) === 0 && (
                    <div className="text-sm text-gray-400">No saved cards — new cards are added during checkout.</div>
                  )}

                  {(methodsQuery.data || []).map((pm) => (
                    <div key={String(pm.id)} className="flex items-center justify-between p-3 bg-[hsl(226,26%,7%)] border border-gray-700 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-md bg-[#0F1623] border border-[#1E2A44]">
                          <span className="text-sm font-semibold">{(pm.scheme || 'Card')[0]}</span>
                        </div>
                        <div>
                          <div className="text-sm font-semibold">{pm.scheme || 'Card'} •••• {pm.last4 || '—'}</div>
                          <div className="text-xs text-gray-400">Expires {pm.exp_month && pm.exp_year ? `${String(pm.exp_month).padStart(2,'0')}/${String(pm.exp_year).slice(-2)}` : '—'}</div>
                          {pm.is_default ? <div className="text-[10px] text-green-400">Default</div> : null}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {!pm.is_default && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => makeDefault.mutate(
                              { id: pm.id },
                              {
                                onSuccess: () => toast({ title: 'Primary card set', description: `${pm.scheme || 'Card'} •••• ${pm.last4}` }),
                                onError: (e: any) => toast({ title: 'Failed to set default', description: e?.message || 'Try again', variant: 'destructive' })
                              }
                            )}
                            disabled={makeDefault.isPending}
                          >
                            Make default
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          aria-label="Delete card"
                          onClick={() => setConfirmDelete({ open: true, id: pm.id })}
                          disabled={deleteMethod.isPending}
                          className="flex items-center justify-center gap-0 sm:gap-2 w-9 h-9 sm:w-auto"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="hidden sm:inline">Delete</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-2 text-xs text-gray-300">
                      <ShieldCheck className="w-4 h-4 text-green-400" />
                      <span>Your card data is processed by Paddle.com; we never store your PAN/CVV.</span>
                    </div>
                    {/* Add Tokens button removed per request */}
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Delete card confirm */}
            <Dialog open={confirmDelete.open} onOpenChange={(o)=> setConfirmDelete(p=>({ ...p, open: o }))}>
              <DialogContent className="bg-[hsl(215,15%,18%)] border-gray-700">
                <DialogHeader>
                  <DialogTitle className="text-white">Remove Card</DialogTitle>
                  <DialogDescription className="text-[hsl(210,9%,91%)]">Are you sure you want to remove this payment method?</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" className="text-white" onClick={()=> setConfirmDelete({ open: false })}>Cancel</Button>
                  <Button variant="destructive" className="text-white" onClick={()=> { if (confirmDelete.id!=null) removePaymentMethod(confirmDelete.id); setConfirmDelete({ open: false }); }}>Remove</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Add Card Modal removed */}

            {/* Checkout result modal */}
            <Dialog open={checkoutModal.open} onOpenChange={(o)=> setCheckoutModal(p=> ({ ...p, open: o }))}>
              <DialogContent className="bg-[hsl(215,15%,18%)] border-gray-700">
                <DialogHeader>
                  <DialogTitle className={checkoutModal.success ? 'text-green-400' : 'text-red-400'}>
                    {checkoutModal.success ? 'Payment Successful' : 'Payment Failed'}
                  </DialogTitle>
                  <DialogDescription className="text-[hsl(210,9%,91%)]">
                    {checkoutModal.message || (checkoutModal.success ? 'Your payment was successful.' : 'Your payment could not be completed.')}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button onClick={()=> setCheckoutModal({ open: false })} className="text-white">Close</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Transaction History */}
            <Card className="bg-[hsl(215,15%,18%)] border-gray-700 animate-slide-in">
              <CardHeader 
                className="cursor-pointer hover:bg-[hsl(215,15%,20%)] transition-colors"
                onClick={() => setIsHistoryOpen(!isHistoryOpen)}
              >
                <CardTitle className="text-white flex items-center justify-between">
                  <div className="flex items-center">
                    <CreditCard className="w-5 h-5 mr-2 text-[hsl(193,100%,50%)]" />
                    Transaction History
                  </div>
                  {isHistoryOpen ? (
                    <ChevronUp className="w-5 h-5 text-[hsl(193,100%,50%)] transition-transform" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[hsl(193,100%,50%)] transition-transform" />
                  )}
                </CardTitle>
              </CardHeader>
              {isHistoryOpen && (
                <CardContent className="space-y-3 animate-in slide-in-from-top-2 duration-200">
                  {historyQuery.isLoading && <div className="text-sm text-gray-400">Loading history…</div>}
                  {historyQuery.isError && <div className="text-sm px-3 py-2 rounded bg-red-600/20 border border-red-500/40 text-white">Failed to load history.</div>}
                  {!historyQuery.isLoading && (historyQuery.data?.length ?? 0) === 0 && (
                    <div className="text-sm text-gray-400">No transactions yet.</div>
                  )}

                  {historyQuery.data && historyQuery.data.length > 0 && (
                    (() => {
                      const all = (historyQuery.data || []);
                      const successStatuses = new Set(['SUCCESS','SUCCEEDED','PAID']);
                      const failedStatuses = new Set(['FAILED','DECLINED','ERROR','CANCELLED','CANCELED']);
                      const filtered = all.filter(h => successStatuses.has(String(h.status||'').toUpperCase()) || failedStatuses.has(String(h.status||'').toUpperCase()));
                      if (!filtered.length) return <div className="text-sm text-gray-400">No completed transactions yet.</div>;

                      const sorted = [...filtered].sort((a,b)=>{
                        const aDate = new Date(a.completed_at || a.created_at || 0).getTime();
                        const bDate = new Date(b.completed_at || b.created_at || 0).getTime();
                        if (sortBy==='date') return sortDir==='asc' ? aDate - bDate : bDate - aDate;
                        if (sortBy==='amount') return sortDir==='asc' ? (a.amount||0) - (b.amount||0) : (b.amount||0) - (a.amount||0);
                        if (sortBy==='package') return (a.package_name||a.package_code||'').localeCompare(b.package_name||b.package_code||'') * (sortDir==='asc'?1:-1);
                        return 0;
                      });

                      return (
                        <div className="w-full">
                          <div className="text-[10px] text-gray-400 mb-2">Sorted by {sortBy} ({sortDir}) • Click headers to change</div>
                          <div className="overflow-x-auto border border-gray-700 rounded-lg">
                            <div className="max-h-[600px] overflow-y-auto">
                              <table className="min-w-full text-xs border-collapse">
                                <thead className="sticky top-0 z-10 bg-[hsl(215,15%,20%)]">
                                  <tr className="text-left text-gray-300">
                                    <th className="py-1.5 px-2 border border-gray-700 cursor-pointer" onClick={()=>{ setSortBy('date'); setSortDir(d=> sortBy==='date' && d==='desc'?'asc':'desc'); }}>
                                      <div className="flex items-center gap-1">Date <ArrowUpDown className="w-3 h-3" /></div>
                                    </th>
                                    <th className="py-1.5 px-2 border border-gray-700 cursor-pointer" onClick={()=>{ setSortBy('package'); setSortDir(d=> sortBy==='package' && d==='desc'?'asc':'desc'); }}>
                                      <div className="flex items-center gap-1">Package <ArrowUpDown className="w-3 h-3" /></div>
                                    </th>
                                    <th className="py-1.5 px-2 border border-gray-700">Tokens</th>
                                    <th className="py-1.5 px-2 border border-gray-700 cursor-pointer" onClick={()=>{ setSortBy('amount'); setSortDir(d=> sortBy==='amount' && d==='desc'?'asc':'desc'); }}>
                                      <div className="flex items-center gap-1">Amount <ArrowUpDown className="w-3 h-3" /></div>
                                    </th>
                                    <th className="py-1.5 px-2 border border-gray-700">Payment Method</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sorted.map((h, idx) => {
                                    const statusUpper = String(h.status||'').toUpperCase();
                                    const isSuccess = successStatuses.has(statusUpper);
                                    const date = new Date(h.completed_at || h.created_at || Date.now());
                                    const m = h.payment_method_id != null ? methodsById.get(String(h.payment_method_id)) : null;
                                    const rawLast4 = (h as any).card_last4 || (h as any).last4 || (h as any).card_last_four || m?.last4 || null;
                                    const resolvedLast4 = rawLast4 ? String(rawLast4).replace(/\D/g, '').slice(-4) : null;
                                    // Remove unconditional 'Card' fallback so missing data shows as failed.
                                    const resolvedScheme = (h as any).card_scheme || (h as any).card_brand || (h as any).brand || m?.scheme || '';
                                    const method = resolvedLast4 ? `${resolvedScheme} •••• ${resolvedLast4}` : (resolvedScheme ? resolvedScheme : 'error');
                                    const amountStr = `${h.currency || 'USD'} ${Number(h.amount||0).toFixed(2)}`;
                                    const colorCls = isSuccess ? 'text-green-400' : 'text-red-400';
                                    return (
                                      <tr key={String(h.id || h.provider_txn_id || Math.random())} className={`${idx % 2 === 0 ? 'bg-[#0F1623]' : 'bg-[#0B1220]'} hover:bg-[hsl(215,15%,22%)]/40`}> 
                                        <td className={`py-1.5 px-2 border border-gray-800 whitespace-nowrap align-middle ${colorCls}`}>{format(date,'dd/MM/yyyy')}</td>
                                        <td className={`py-1.5 px-2 border border-gray-800 align-middle ${colorCls}`}>{h.package_name || h.package_code || '—'}</td>
                                        <td className={`py-1.5 px-2 border border-gray-800 align-middle ${colorCls}`}>{h.credits_granted ?? '—'}</td>
                                        <td className={`py-1.5 px-2 border border-gray-800 align-middle ${colorCls}`}>{amountStr}</td>
                                        <td className={`py-1.5 px-2 border border-gray-800 align-middle ${colorCls}`}>{method}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      );
                    })()
                  )}
                  <div className="flex items-center gap-2 text-xs text-gray-300">
                    <ShieldCheck className="w-4 h-4 text-green-400" />
                    <span>Your payments are secure (PCI DSS).</span>
                  </div>
                </CardContent>
              )}
            </Card>


            {/* Devices & Active Sessions */}
            <Card className="bg-[hsl(215,15%,18%)] border-gray-700 animate-slide-in">
              <CardHeader 
                className="cursor-pointer hover:bg-[hsl(215,15%,20%)] transition-colors"
                onClick={() => setIsDevicesSectionOpen(!isDevicesSectionOpen)}
              >
                <CardTitle className="text-white flex items-center justify-between w-full">
                  <div className="flex items-center">
                    <Globe className="w-5 h-5 mr-2 text-[hsl(193,100%,50%)]" />
                    Active Devices / Sessions
                    <span className="text-xs text-gray-300 font-normal ml-2">{uniqueCount} unique / {totalActive} shown{showAllSessions && ' (all)'}</span>
                  </div>
                  {isDevicesSectionOpen ? (
                    <ChevronUp className="w-5 h-5 text-[hsl(193,100%,50%)] transition-transform" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[hsl(193,100%,50%)] transition-transform" />
                  )}
                </CardTitle>
              </CardHeader>
              {isDevicesSectionOpen && (
                <CardContent className="space-y-3 animate-in slide-in-from-top-2 duration-200">
                  <div className="flex items-center justify-between flex-wrap gap-3 text-xs">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" className="accent-[hsl(193,100%,50%)]" checked={showAllSessions} onChange={e=>setShowAllSessions(e.target.checked)} />
                      Show all sessions
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" className="accent-[hsl(193,100%,50%)]" checked={revealDeviceIds} onChange={e=>setRevealDeviceIds(e.target.checked)} />
                      Reveal device_id
                    </label>
                  </div>
                  {sessionsQuery?.isLoading && <div className="text-sm text-gray-400">Loading sessions...</div>}
                  {sessionsQuery?.isError && <div className="text-sm px-3 py-2 rounded bg-red-600/20 border border-red-500/40 text-white">Failed to load sessions.</div>}
                  {!sessionsQuery?.isLoading && devices.length === 0 && <div className="text-sm text-gray-400">No active sessions.</div>}
                  {devices.map((d: any) => {
                    const lastActive = d.lastSeen ? formatDistanceToNow(new Date(d.lastSeen), { addSuffix: true }) : 'Unknown';
                    const expires = d.raw?.expires_at ? formatDistanceToNow(new Date(d.raw.expires_at), { addSuffix: true }) : null;
                    return (
                      <div key={d.id} className={`flex items-start justify-between p-3 bg-[hsl(226,26%,7%)] border rounded-lg ${d.isCurrent ? 'border-blue-500' : 'border-gray-700'}`}>
                        <div className="flex items-start gap-3 w-full">
                          <div className="w-8 h-8 flex items-center justify-center rounded-md bg-[#0F1623] border border-[#1E2A44] mt-0.5">{d.icon === 'phone' ? <Smartphone className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}</div>
                          <div className="text-sm w-full">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold">{d.name}</span>
                              {d.isCurrent && <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-600 rounded-full">Current</span>}
                              {d.raw?.remember_me && <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">Remember</span>}
                              {revealDeviceIds && d.raw?.device_id && <span className="px-2 py-0.5 text-[10px] bg-gray-700/40 rounded select-all font-mono">{d.raw.device_id}</span>}
                            </div>
                            <div className="text-xs text-gray-400 mt-1 space-y-0.5">
                              <div>{d.location} • {d.ip}</div>
                              <div>Last active {lastActive}{expires && <> • Expires {expires}</>}</div>
                              {d.raw?.browser && d.raw?.os && <div>{d.raw.browser} • {d.raw.os}</div>}
                            </div>
                          </div>
                        </div>
                        {!d.isCurrent && (
                          <div className="ml-3">
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={revokeSessionMutation?.isPending}
                              onClick={() => setRevokeDialog({ open: true, sessionId: d.id })}
                              className="text-xs text-red-400 hover:text-red-500"
                            >
                              {revokeSessionMutation?.isPending && revokeSessionMutation?.variables === d.id ? (
                                <>
                                  <LoadingSpinner className="w-3.5 h-3.5 mr-1.5" />
                                  Logging Out...
                                </>
                              ) : (
                                'Logout'
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              )}
            </Card>
            {/* Revoke Session Confirmation */}
            <Dialog open={revokeDialog.open} onOpenChange={(o) => setRevokeDialog(prev => ({ ...prev, open: o }))}>
              <DialogContent className="bg-[hsl(215,15%,18%)] border-gray-700">
                <DialogHeader>
                  <DialogTitle className="text-white">Log Out Device</DialogTitle>
                  <DialogDescription className="text-[hsl(210,9%,91%)]">Are you sure you want to log out this device?</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setRevokeDialog({ open: false, sessionId: null })} className="border-gray-600 text-[hsl(210,9%,91%)]">Cancel</Button>
                  <Button
                    // Yellow styled Log Out button for device revoke
                    variant="default"
                    className="bg-amber-400 hover:bg-amber-500 text-[#0B1220] disabled:opacity-60"
                    disabled={revokeSessionMutation?.isPending}
                    onClick={() => {
                      if (revokeDialog.sessionId != null) {
                        revokeSessionMutation.mutate(Number(revokeDialog.sessionId), {
                          onSettled: () => setRevokeDialog({ open: false, sessionId: null }),
                          onSuccess: (_d: any, sid: any) => {
                            if (sid === currentSessionId) {
                              logout();
                              window.location.href = '/login';
                            }
                          }
                        });
                      }
                    }}
                  >
                    {revokeSessionMutation?.isPending ? (
                      <>
                        <LoadingSpinner className="w-4 h-4 mr-2" />
                        Logging Out...
                      </>
                    ) : (
                      'Log Out'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Account Actions */}
            <Card className="bg-[hsl(215,15%,18%)] border-gray-700 animate-slide-in">
              <CardHeader><CardTitle className="text-white flex items-center"><AlertTriangle className="w-5 h-5 mr-2 text-[hsl(0,72%,51%)]" />Account Actions</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-[hsl(0,72%,51%,0.1)] border border-[hsl(0,72%,51%)] rounded-lg">
                  <div>
                    <h3 className="text-white font-semibold">Sign Out</h3>
                    <p className="text-[hsl(210,9%,91%)] text-sm">Sign out of your TradeSagePro account</p>
                  </div>
                  <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
                    <DialogTrigger asChild>
                      <Button variant="destructive" className="bg-[hsl(0,72%,51%)] text-white hover:bg-[hsl(0,72%,45%)]"><LogOut className="w-4 h-4 mr-2" />Sign Out</Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[hsl(215,15%,18%)] border-gray-700">
                      <DialogHeader>
                        <DialogTitle className="text-white">Confirm Sign Out</DialogTitle>
                        <DialogDescription className="text-[hsl(210,9%,91%)]">Are you sure you want to sign out of your account?</DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowLogoutConfirm(false)} className="border-gray-600 text-[hsl(210,9%,91%)]">Cancel</Button>
                        <Button variant="destructive" onClick={handleLogout} disabled={logoutMutation?.isPending || revokeSessionMutation?.isPending} className="bg-[hsl(0,72%,51%)] text-white">
                          {logoutMutation?.isPending || revokeSessionMutation?.isPending ? (
                            <>
                              <LoadingSpinner className="w-4 h-4 mr-2" />
                              Signing Out...
                            </>
                          ) : (
                            <>
                              <LogOut className="w-4 h-4 mr-2" />
                              Sign Out
                            </>
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>

            {/* Account Info */}
            <Card className="bg-[hsl(215,15%,18%)] border-gray-700 animate-slide-in">
              <CardContent className="pt-6">
                <div className="text-center text-[hsl(210,9%,91%)] text-sm">
                  <p>Account created: {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : ''}</p>
                  <p className="mt-2">Tokens available: <span className="text-[hsl(193,100%,50%)] font-semibold">{user.credits ?? 0}</span></p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}